import {useState} from "react";

import {Button} from "@/components/ui/button";
import {Card, CardContent} from "@/components/ui/card";
import {Loader} from "@/src/modules/core/components";
import useResidentHouseholdId from "@/src/modules/core/hooks/use-resident-household-id";
import useResidentMembership from "@/src/modules/core/hooks/use-resident-membership";
import {formatDate} from "@/src/modules/core/lib/formatters";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import useHousehold from "@/src/modules/households/hooks/use-household";
import {getHouseholdOccupantMembership} from "@/src/modules/households/lib/household-membership";
import {householdService} from "@/src/modules/households/service";
import {cn} from "@/lib/utils";

import {
  RESIDENT_HOME_BLOCK_TITLE_CLASS,
  RESIDENT_HOME_CARD_CONTENT_CLASS,
  RESIDENT_HOME_CARD_SHELL_CLASS,
  RESIDENT_HOME_PILLAR_HEADER_ROW_CLASS,
  RESIDENT_HOME_TEXT_INSET_CLASS,
} from "../lib/resident-home-block-layout";
import {RESIDENT_HOME_TENANT_ICON} from "../lib/resident-home-teaser";
import {InviteTenantSheet} from "./invite-tenant-sheet";
import {ResidentHomeIconTextBlock} from "./resident-home-icon-text-block";
import {RevokeTenantSheet} from "./revoke-tenant-sheet";

type OccupantField = {
  label: string;
  value: string;
};

function OccupantFieldRow({label, value}: OccupantField) {
  return (
    <div className="min-w-0">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="text-foreground truncate text-sm">{value}</p>
    </div>
  );
}

export function ResidentHomeTenantCard() {
  const userProperty = useStoredUserProperty();
  const householdId = useResidentHouseholdId();
  const membership = useResidentMembership();
  const {household, isPending} = useHousehold(householdId);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const canRevoke =
    Boolean(membership?.is_owner && !membership.is_occupant) &&
    Boolean(householdId) &&
    Boolean(userProperty?.id) &&
    householdService.getCanRevokeOccupant();

  const canInvite =
    Boolean(membership?.is_owner && membership.is_occupant) &&
    Boolean(householdId) &&
    Boolean(userProperty?.id) &&
    householdService.getCanInviteOccupant();

  if (!canRevoke && !canInvite) {
    return null;
  }

  const occupant = getHouseholdOccupantMembership(household?.memberships);

  if (canRevoke && !isPending && !occupant) {
    return null;
  }

  const email = occupant?.email?.trim() || null;
  const occupyingSince = occupant?.started_at ? formatDate(occupant.started_at, true) : null;
  const fields: OccupantField[] = occupant
    ? [
        {label: "Name", value: occupant.name?.trim() || "—"},
        ...(email ? [{label: "Email", value: email}] : []),
        ...(occupyingSince ? [{label: "Occupying since", value: occupyingSince}] : []),
      ]
    : [];
  const fieldGridClass =
    fields.length >= 3 ? "sm:grid-cols-3" : fields.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-1";

  return (
    <>
      <Card className={RESIDENT_HOME_CARD_SHELL_CLASS}>
        <CardContent className={cn(RESIDENT_HOME_CARD_CONTENT_CLASS, "flex flex-col gap-3 pb-5")}>
          <div className={RESIDENT_HOME_PILLAR_HEADER_ROW_CLASS}>
            <ResidentHomeIconTextBlock
              icon={RESIDENT_HOME_TENANT_ICON.icon}
              iconWrapperClassName={RESIDENT_HOME_TENANT_ICON.iconWrapperClassName}
              iconClassName={RESIDENT_HOME_TENANT_ICON.iconClassName}
              className="min-w-0 flex-1 items-start"
              title={<p className={RESIDENT_HOME_BLOCK_TITLE_CLASS}>Current tenant</p>}
            />
            {canInvite ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="shrink-0"
                onClick={() => setInviteOpen(true)}
              >
                Invite tenant
              </Button>
            ) : occupant ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-destructive/40 text-destructive hover:bg-destructive/10 shrink-0"
                onClick={() => setRevokeOpen(true)}
              >
                End tenancy
              </Button>
            ) : null}
          </div>

          {canInvite ? (
            <p className={cn(RESIDENT_HOME_TEXT_INSET_CLASS, "text-muted-foreground text-sm")}>
              No tenant assigned to this unit.
            </p>
          ) : isPending ? (
            <Loader className={cn(RESIDENT_HOME_TEXT_INSET_CLASS, "min-h-12")} />
          ) : (
            <div className={cn(RESIDENT_HOME_TEXT_INSET_CLASS, "grid grid-cols-1 gap-3", fieldGridClass)}>
              {fields.map((field) => (
                <OccupantFieldRow key={field.label} label={field.label} value={field.value} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {canInvite && userProperty?.id && householdId ? (
        inviteOpen ? (
          <InviteTenantSheet
            open
            householdId={householdId}
            contextPropertyId={userProperty.id}
            onClose={() => setInviteOpen(false)}
          />
        ) : null
      ) : null}

      <RevokeTenantSheet
        open={revokeOpen}
        householdId={householdId}
        contextPropertyId={userProperty?.id ?? null}
        onClose={() => setRevokeOpen(false)}
      />
    </>
  );
}
